/**
 * Brain Missions · API client — Milestone 37.4.
 *
 * Thin typed layer over the backend endpoints from Phase 1:
 *   GET  /workspaces/{ws}/brain-missions/{type}/catalog
 *   GET  /workspaces/{ws}/brain-missions/{type}/state
 *   POST /workspaces/{ws}/brain-missions/{type}/answer
 *   POST /workspaces/{ws}/brain-missions/{type}/skip
 *   POST /workspaces/{ws}/brain-missions/{type}/submit-for-review
 *   POST /workspaces/{ws}/brain-missions/{type}/approve
 *   GET  /workspaces/{ws}/brain-missions/gate
 */
import { http } from "@/shared/api/http";

export type BrainMissionType = "business_brain" | "brand_brain";
export type QuestionCategory = "required" | "recommended" | "optional";
export type QuestionKind = "short_text" | "long_text" | "string_list" | "choice";

export interface BrainQuestion {
  id: string;
  mission_type: BrainMissionType;
  category: QuestionCategory;
  kind: QuestionKind;
  section: string;
  field_path: string;
  label: string;
  why_it_matters: string;
  impact_of_skipping: string;
  example_answers: string[];
  choices: string[];
}

export interface BrainCatalog {
  mission_type: BrainMissionType;
  total: number;
  required_total: number;
  questions: BrainQuestion[];
}

export interface BrainMissionState {
  mission_type: BrainMissionType;
  workspace_id: string;
  answered_ids: string[];
  skipped_ids: string[];
  remaining_required_ids: string[];
  current_question_id: string | null;
  total_questions: number;
  answered_count: number;
  required_answered: number;
  required_total: number;
  derived_stage: "started" | "context" | "execution" | "decision" | "confirmation";
  brain_status: "draft" | "pending_review" | "approved" | "missing";
  brain_version: number | null;
  eligible_for_review: boolean;
  eligible_for_approval: boolean;
}

export interface BrainGate {
  business_brain_approved: boolean;
  brand_brain_approved: boolean;
  business_missing_required: string[];
  brand_missing_required: string[];
  next_recommended_mission: "business_brain" | "brand_brain" | "campaign" | null;
}

const base = (ws: string, type: BrainMissionType) =>
  `/workspaces/${ws}/brain-missions/${type}`;

export function fetchBrainCatalog(ws: string, type: BrainMissionType) {
  return http<BrainCatalog>(`${base(ws, type)}/catalog`);
}

export function fetchBrainState(ws: string, type: BrainMissionType) {
  return http<BrainMissionState>(`${base(ws, type)}/state`);
}

export function applyBrainAnswer(
  ws: string,
  type: BrainMissionType,
  input: { question_id: string; value: unknown },
) {
  return http<BrainMissionState>(`${base(ws, type)}/answer`, {
    method: "POST",
    body: input,
  });
}

export function skipBrainQuestion(
  ws: string,
  type: BrainMissionType,
  input: { question_id: string; skipped?: boolean },
) {
  return http<BrainMissionState>(`${base(ws, type)}/skip`, {
    method: "POST",
    body: { skipped: true, ...input },
  });
}

export function submitBrainForReview(ws: string, type: BrainMissionType) {
  return http<{ state: BrainMissionState }>(`${base(ws, type)}/submit-for-review`, {
    method: "POST",
  });
}

export function approveBrain(ws: string, type: BrainMissionType) {
  return http<{ state: BrainMissionState }>(`${base(ws, type)}/approve`, {
    method: "POST",
  });
}

export function fetchBrainGate(ws: string) {
  return http<BrainGate>(`/workspaces/${ws}/brain-missions/gate`);
}
